import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, CalendarDays, CalendarClock, Wallet,
  Banknote, ClipboardList, ScrollText, Settings as SettingsIcon, LogOut, Menu, X, FileBarChart, Receipt,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const items = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/employees", icon: Users, label: "Employees" },
  { to: "/admin/branches", icon: Building2, label: "Branches" },
  { to: "/admin/attendance", icon: ClipboardList, label: "Attendance" },
  { to: "/admin/leaves", icon: CalendarClock, label: "Leaves" },
  { to: "/admin/holidays", icon: CalendarDays, label: "Holidays" },
  { to: "/admin/advances", icon: Banknote, label: "Advances" },
  { to: "/admin/reimbursements", icon: Receipt, label: "Reimbursements" },
  { to: "/admin/payroll", icon: Wallet, label: "Payroll" },
  { to: "/admin/reports", icon: FileBarChart, label: "Reports" },
  { to: "/admin/audit", icon: ScrollText, label: "Audit Log" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SideContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-800">
        <img src="/omsivo-logo.png" alt="Omsivo EMP" className="h-9 w-9 rounded-full" />
        <div>
          <div className="font-head font-extrabold text-white leading-tight">Omsivo EMP</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Admin Console</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${it.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30" : "text-slate-300 hover:bg-slate-800"
              }`
            }
          >
            <it.icon className="h-4.5 w-4.5" />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800 space-y-1">
        <div className="px-3 py-2 text-xs text-slate-400">{user?.name}</div>
        <button data-testid="admin-logout-button" onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 w-full rounded-xl text-sm font-medium text-slate-300 hover:bg-red-600 hover:text-white transition-colors">
          <LogOut className="h-4.5 w-4.5" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <aside className="hidden lg:block w-64 bg-slate-900 fixed inset-y-0 left-0 z-40">
        <SideContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-slate-900"><SideContent /></div>
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="glass sticky top-0 z-30 border-b border-slate-200 h-14 flex items-center px-4 lg:hidden">
          <button data-testid="admin-menu-toggle" onClick={() => setOpen(true)} className="p-2 -ml-2">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <span className="font-head font-extrabold ml-2">Omsivo EMP Admin</span>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
